import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Plane, MapPin, Check } from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

interface TripCreationFormProps {
  onTripCreated: () => void;
  onCancel: () => void;
}

// Popular cities data for autocomplete
const POPULAR_CITIES = [
  { name: 'Paris', country: 'France' },
  { name: 'London', country: 'United Kingdom' },
  { name: 'Tokyo', country: 'Japan' },
  { name: 'New York', country: 'United States' },
  { name: 'Barcelona', country: 'Spain' },
  { name: 'Rome', country: 'Italy' },
  { name: 'Amsterdam', country: 'Netherlands' },
  { name: 'Bangkok', country: 'Thailand' },
  { name: 'Singapore', country: 'Singapore' },
  { name: 'Dubai', country: 'United Arab Emirates' },
  { name: 'Berlin', country: 'Germany' },
  { name: 'Sydney', country: 'Australia' },
  { name: 'Toronto', country: 'Canada' },
  { name: 'Mumbai', country: 'India' },
  { name: 'São Paulo', country: 'Brazil' },
  { name: 'Buenos Aires', country: 'Argentina' },
  { name: 'Istanbul', country: 'Turkey' },
  { name: 'Moscow', country: 'Russia' },
  { name: 'Cairo', country: 'Egypt' },
  { name: 'Cape Town', country: 'South Africa' },
  { name: 'Los Angeles', country: 'United States' },
  { name: 'Miami', country: 'United States' },
  { name: 'Las Vegas', country: 'United States' },
  { name: 'Chicago', country: 'United States' },
  { name: 'San Francisco', country: 'United States' },
  { name: 'Madrid', country: 'Spain' },
  { name: 'Milan', country: 'Italy' },
  { name: 'Vienna', country: 'Austria' },
  { name: 'Prague', country: 'Czech Republic' },
  { name: 'Budapest', country: 'Hungary' },
  { name: 'Warsaw', country: 'Poland' },
  { name: 'Stockholm', country: 'Sweden' },
  { name: 'Oslo', country: 'Norway' },
  { name: 'Copenhagen', country: 'Denmark' },
  { name: 'Helsinki', country: 'Finland' },
  { name: 'Zurich', country: 'Switzerland' },
  { name: 'Brussels', country: 'Belgium' },
  { name: 'Dublin', country: 'Ireland' },
  { name: 'Edinburgh', country: 'United Kingdom' },
  { name: 'Lisbon', country: 'Portugal' },
  { name: 'Athens', country: 'Greece' },
  { name: 'Hong Kong', country: 'Hong Kong' },
  { name: 'Seoul', country: 'South Korea' },
  { name: 'Taipei', country: 'Taiwan' },
  { name: 'Kuala Lumpur', country: 'Malaysia' },
  { name: 'Jakarta', country: 'Indonesia' },
  { name: 'Manila', country: 'Philippines' },
  { name: 'Ho Chi Minh City', country: 'Vietnam' },
  { name: 'Hanoi', country: 'Vietnam' },
  { name: 'Melbourne', country: 'Australia' },
  { name: 'Perth', country: 'Australia' },
  { name: 'Auckland', country: 'New Zealand' },
  { name: 'Vancouver', country: 'Canada' },
  { name: 'Montreal', country: 'Canada' },
  { name: 'Mexico City', country: 'Mexico' },
  { name: 'Cancún', country: 'Mexico' },
  { name: 'Lima', country: 'Peru' },
  { name: 'Rio de Janeiro', country: 'Brazil' },
  { name: 'Bogotá', country: 'Colombia' },
  { name: 'Santiago', country: 'Chile' },
  { name: 'Montevideo', country: 'Uruguay' },
];

export function TripCreationForm({ onTripCreated, onCancel }: TripCreationFormProps) {
  const [formData, setFormData] = useState({
    trip_name: '',
    destination: '',
    destination_country: '',
    start_date: undefined as Date | undefined,
    end_date: undefined as Date | undefined,
    trip_type: '',
    luggage_type: [] as string[],
    weight_unit: 'kg' as 'kg' | 'lbs',
    carry_on_weight: '',
    checked_weight: ''
  });
  const [destinationInput, setDestinationInput] = useState('');
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  const filteredCities = useMemo(() => {
    if (!destinationInput.trim()) return [];
    return POPULAR_CITIES.filter(city =>
      city.name.toLowerCase().includes(destinationInput.toLowerCase()) ||
      city.country.toLowerCase().includes(destinationInput.toLowerCase())
    ).slice(0, 8);
  }, [destinationInput]);

  const selectCity = (city: { name: string; country: string }) => {
    setFormData(prev => ({ 
      ...prev, 
      destination: city.name,
      destination_country: city.country 
    }));
    setDestinationInput(`${city.name}, ${city.country}`);
    setShowCitySuggestions(false);
  };

  const handleDestinationChange = (value: string) => {
    setDestinationInput(value);
    setShowCitySuggestions(true);
    if (!value.trim()) {
      setFormData(prev => ({ ...prev, destination: '', destination_country: '' }));
    }
  };

  const handleLuggageTypeChange = (type: string) => {
    setFormData(prev => ({
      ...prev,
      luggage_type: prev.luggage_type.includes(type)
        ? prev.luggage_type.filter(t => t !== type)
        : [...prev.luggage_type, type]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !formData.start_date || !formData.end_date || !formData.destination) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('trips')
        .insert([{
          title: formData.trip_name || `Trip to ${formData.destination}`,
          destination: formData.destination,
          destination_country: formData.destination_country,
          start_date: formData.start_date.toISOString().split('T')[0],
          end_date: formData.end_date.toISOString().split('T')[0],
          trip_type: formData.trip_type as any,
          user_id: user.id
        }]);

      if (error) throw error;

      toast({
        title: "Trip Created!",
        description: "Your new trip has been created successfully.",
      });
      
      onTripCreated();
    } catch (error) {
      console.error('Error creating trip:', error);
      toast({
        title: "Error",
        description: "Failed to create trip. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Plane className="h-5 w-5" />
          Create New Trip
        </CardTitle>
        <CardDescription>
          Plan your next adventure by creating a new trip
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="trip_name">Trip Name</Label>
            <Input
              id="trip_name"
              value={formData.trip_name}
              onChange={(e) => setFormData(prev => ({ ...prev, trip_name: e.target.value }))}
              placeholder="e.g., Summer vacation, Business trip to Tokyo..."
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">Give your trip a memorable name (optional - defaults to destination name)</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 relative">
              <Label htmlFor="destination">Destination</Label>
              <div className="relative">
                <Input
                  id="destination"
                  value={destinationInput}
                  onChange={(e) => handleDestinationChange(e.target.value)}
                  onFocus={() => setShowCitySuggestions(true)}
                  placeholder="Start typing a city name..."
                  required
                  className="pr-8"
                />
                <MapPin className="absolute right-2 top-2.5 h-4 w-4 text-muted-foreground" />
              </div>
              {showCitySuggestions && filteredCities.length > 0 && (
                <div className="absolute top-full left-0 right-0 z-50 bg-background border border-input rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {filteredCities.map((city, index) => (
                    <button
                      key={`${city.name}-${city.country}`}
                      type="button"
                      onClick={() => selectCity(city)}
                      className="w-full px-3 py-2 text-left hover:bg-accent hover:text-accent-foreground flex items-center justify-between"
                    >
                      <span>{city.name}, {city.country}</span>
                      {formData.destination === city.name && formData.destination_country === city.country && (
                        <Check className="h-4 w-4" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="trip_type">Trip Type</Label>
              <Select value={formData.trip_type} onValueChange={(value) => setFormData(prev => ({ ...prev, trip_type: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select trip type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="leisure">Leisure</SelectItem>
                  <SelectItem value="business">Business</SelectItem>
                  <SelectItem value="adventure">Adventure</SelectItem>
                  <SelectItem value="family">Family</SelectItem>
                  <SelectItem value="romantic">Romantic</SelectItem>
                  <SelectItem value="solo">Solo Travel</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal pr-8",
                      !formData.start_date && "text-muted-foreground"
                    )}
                  >
                    {formData.start_date ? format(formData.start_date, "PPP") : "Pick start date"}
                    <CalendarIcon className="absolute right-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={formData.start_date}
                    onSelect={(date) => setFormData(prev => ({ ...prev, start_date: date }))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal pr-8",
                      !formData.end_date && "text-muted-foreground"
                    )}
                  >
                    {formData.end_date ? format(formData.end_date, "PPP") : "Pick end date"}
                    <CalendarIcon className="absolute right-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={formData.end_date}
                    onSelect={(date) => setFormData(prev => ({ ...prev, end_date: date }))}
                    initialFocus
                    disabled={(date) => formData.start_date ? date < formData.start_date : false}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="space-y-4">
            <Label>Luggage Configuration</Label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Luggage Type</Label>
                <div className="space-y-2">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.luggage_type.includes('carry-on')}
                      onChange={() => handleLuggageTypeChange('carry-on')}
                      className="rounded border-input"
                    />
                    <span className="text-sm">Carry-on</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.luggage_type.includes('checked')}
                      onChange={() => handleLuggageTypeChange('checked')}
                      className="rounded border-input"
                    />
                    <span className="text-sm">Checked bag</span>
                  </label>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Weight Unit</Label>
                <Select value={formData.weight_unit} onValueChange={(value: 'kg' | 'lbs') => setFormData(prev => ({ ...prev, weight_unit: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kg">Kilograms (kg)</SelectItem>
                    <SelectItem value="lbs">Pounds (lbs)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Weight Allowances</Label>
                <div className="space-y-2">
                  {formData.luggage_type.includes('carry-on') && (
                    <div>
                      <Label htmlFor="carry_on_weight" className="text-xs">Carry-on limit</Label>
                      <Input
                        id="carry_on_weight"
                        type="number"
                        value={formData.carry_on_weight}
                        onChange={(e) => setFormData(prev => ({ ...prev, carry_on_weight: e.target.value }))}
                        placeholder={`0 ${formData.weight_unit}`}
                        min="0"
                        step="0.1"
                        className="text-sm"
                      />
                    </div>
                  )}
                  {formData.luggage_type.includes('checked') && (
                    <div>
                      <Label htmlFor="checked_weight" className="text-xs">Checked bag limit</Label>
                      <Input
                        id="checked_weight"
                        type="number"
                        value={formData.checked_weight}
                        onChange={(e) => setFormData(prev => ({ ...prev, checked_weight: e.target.value }))}
                        placeholder={`0 ${formData.weight_unit}`}
                        min="0"
                        step="0.1"
                        className="text-sm"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="submit" disabled={loading} className="flex-1">
              {loading ? 'Creating Trip...' : 'Create Trip'}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}